import { untag } from "./typeUtils.mjs";

//#region src/dateUtils.ts
const msInSecond = 1e3;
const secondsInMinute = 60;
const minutesInHour = 60;
const hoursInDay = 24;
const daysInWeek = 7;
const formatter = new Intl.DateTimeFormat("en-US", {
	dateStyle: "medium",
	timeStyle: "short"
});
/**
* Formats a date using the default medium date and short time style.
* @example formatDate(new Date()) // "Jan 5, 2025, 2:30 PM"
*/
function formatDate(date) {
	return formatter.format(date);
}
const dateOnlyFormatter = new Intl.DateTimeFormat("en-US", {
	month: "long",
	day: "numeric",
	year: "numeric"
});
/**
* Formats a date with only the date (no time).
* @example formatDateOnly(new Date("1981-06-12")) // "June 12, 1981"
*/
function formatDateOnly(date) {
	return dateOnlyFormatter.format(date);
}
/**
* Formats a Unix timestamp using the default medium date and short time style.
* @example formatUnixTimestamp(1704441000) // "Jan 5, 2024, 2:30 PM"
*/
function formatUnixTimestamp(unixTimestamp) {
	return formatter.format(new Date(untag(unixTimestamp * msInSecond)));
}
/**
* Formats a date with detailed timestamp including date and time in 12-hour format.
* @example formatTimestampAbsolute(new Date()) // "Jan 5, 2025, 2:30 PM"
*/
function formatTimestampAbsolute(date) {
	return date.toLocaleDateString("en-US", {
		month: "short",
		day: "numeric",
		year: "numeric",
		hour: "numeric",
		minute: "2-digit",
		hour12: true
	});
}
const minutesInDay = minutesInHour * hoursInDay;
const minutesInWeek = minutesInDay * daysInWeek;
/**
* Formats a timestamp as a relative time string for recent activity.
* @param timestamp - The timestamp to format
* @param referenceDate - Optional reference date to calculate relative time from (defaults to now)
* @example formatTimestampRelative(new Date()) // "just now"
* @example formatTimestampRelative(new Date(Date.now() - 5 * 60 * 1000)) // "5min ago"
* @example formatTimestampRelative(new Date(Date.now() - 3 * 60 * 60 * 1000)) // "3h ago"
* @example formatTimestampRelative(new Date(Date.now() - 2 * 24 * 60 * 60 * 1000)) // "2d ago"
* @example formatTimestampRelative(new Date(Date.now() - 10 * 24 * 60 * 60 * 1000)) // "1w ago"
*/
function formatTimestampRelative(timestamp, referenceDate) {
	const diffMs = (referenceDate ? referenceDate.getTime() : Date.now()) - timestamp.getTime();
	const diffMins = Math.floor(diffMs / (msInSecond * secondsInMinute));
	if (diffMs > 0) {
		if (diffMins < 1) return "just now";
		if (diffMins < minutesInHour) return `${diffMins}min ago`;
		if (diffMins < minutesInDay) return `${Math.floor(diffMins / minutesInHour)}h ago`;
		if (diffMins < minutesInWeek) return `${Math.floor(diffMins / minutesInDay)}d ago`;
		return `${Math.floor(diffMins / minutesInWeek)}w ago`;
	} else {
		if (diffMins > -minutesInHour) return `${Math.floor(Math.abs(diffMins))}min`;
		if (diffMins > -minutesInDay) return `${Math.floor(Math.abs(diffMins / minutesInHour))}h`;
		if (diffMins > -minutesInWeek) return `${Math.floor(Math.abs(diffMins / minutesInDay))}d`;
		return `${Math.floor(Math.abs(diffMins / minutesInWeek))}w`;
	}
}

//#endregion
export { formatDate, formatDateOnly, formatTimestampAbsolute, formatTimestampRelative, formatUnixTimestamp };